import React, { useState, useEffect } from 'react';
import {
    Table,
    Button,
    Input,
    Space,
    Tag,
    Modal,
    Form,
    Row,
    Col,
    Select,
    message,
    Popconfirm,
    Card
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    SearchOutlined,
    UserOutlined,
    EyeOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { patientService, dentistService } from '../../services';
import { useApp } from '../../context/AppContext';

const { Option } = Select;
const { TextArea } = Input;

const Patients = () => {
    const navigate = useNavigate();
    const { t } = useTranslation(['patient', 'common']);
    const { user } = useApp();
    const isDentist = user?.role === 'dentist';
    const canViewPatientDetails = user?.role === 'admin' || user?.role === 'dentist';
    const [patients, setPatients] = useState([]);
    const [dentists, setDentists] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingPatient, setEditingPatient] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [form] = Form.useForm();

    useEffect(() => {
        fetchPatients();
        fetchDentists();
    }, [pagination.current, searchText]);

    const fetchDentists = async () => {
        try {
            const response = await dentistService.getAll({ limit: 1000, isActive: true });
            if (response.success) {
                setDentists(response.data);
            }
        } catch (error) {
            console.error('Error fetching dentists:', error);
            message.error(t('common:errors.fetchFailed', 'Failed to fetch data'));
        }
    };

    const fetchPatients = async () => {
        setLoading(true);
        try {
            const response = await patientService.getAll({
                page: pagination.current,
                limit: pagination.pageSize,
                search: searchText
            });
            if (response.success) {
                setPatients(response.data);
                setPagination(prev => ({
                    ...prev,
                    total: response.total
                }));
            }
        } catch (error) {
            message.error(t('patient:messages.fetchFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleTableChange = (newPagination) => {
        setPagination(newPagination);
    };

    const handleSearch = (value) => {
        setSearchText(value);
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const showModal = (patient = null) => {
        setEditingPatient(patient);
        if (patient) {
            form.setFieldsValue({
                ...patient,
                dateOfBirth: patient.dateOfBirth ? new Date(patient.dateOfBirth).toISOString().split('T')[0] : null,
                dentistIds: patient.dentistIds?.map(d => typeof d === 'object' ? d._id : d) || []
            });
        } else {
            form.resetFields();
        }
        setModalVisible(true);
    };

    const handleCancel = () => {
        setModalVisible(false);
        setEditingPatient(null);
        form.resetFields();
    };

    const handleSubmit = async (values) => {
        try {
            if (editingPatient) {
                await patientService.update(editingPatient._id, values);
                message.success(t('patient:messages.updateSuccess'));
            } else {
                await patientService.create(values);
                message.success(t('patient:messages.createSuccess'));
            }
            handleCancel();
            fetchPatients();
        } catch (error) {
            message.error(error.response?.data?.message || t('patient:messages.operationFailed'));
        }
    };

    const handleDelete = async (id) => {
        try {
            await patientService.delete(id);
            message.success(t('patient:messages.deleteSuccess'));
            fetchPatients();
        } catch (error) {
            message.error(t('patient:messages.deleteFailed'));
        }
    };

    const columns = [
        {
            title: t('patient:name'),
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <Space>
                    <UserOutlined />
                    {text}
                </Space>
            )
        },
        {
            title: t('patient:age'),
            dataIndex: 'age',
            key: 'age',
            width: 80
        },
        {
            title: t('patient:gender'),
            dataIndex: 'gender',
            key: 'gender',
            width: 100,
            render: (gender) => (
                <Tag color={gender === 'Male' ? 'blue' : gender === 'Female' ? 'pink' : 'default'}>
                    {gender}
                </Tag>
            )
        },
        {
            title: t('patient:contact'),
            dataIndex: 'contact',
            key: 'contact'
        },
        {
            title: t('patient:status'),
            dataIndex: 'isActive',
            key: 'isActive',
            width: 100,
            render: (isActive) => (
                <Tag color={isActive ? 'success' : 'error'}>
                    {isActive ? t('patient:active') : t('patient:inactive')}
                </Tag>
            )
        },
        {
            title: t('common:actions'),
            key: 'actions',
            width: 200,
            render: (_, record) => (
                <Space>
                    {canViewPatientDetails && (
                        <Button
                            type="primary"
                            icon={<EyeOutlined />}
                            onClick={() => navigate(`/patients/${record._id}`)}
                            size="small"
                        >
                            {t('common:view')}
                        </Button>
                    )}
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => showModal(record)}
                    >
                        {t('common:edit')}
                    </Button>
                    {canViewPatientDetails && (
                        <Popconfirm
                            title={t('patient:messages.deleteConfirm')}
                            onConfirm={() => handleDelete(record._id)}
                            okText={t('common:yes')}
                            cancelText={t('common:no')}
                        >
                            <Button type="link" danger icon={<DeleteOutlined />}>
                                {t('common:delete')}
                            </Button>
                        </Popconfirm>
                    )}
                </Space>
            )
        }
    ];

    return (
        <div>
            <Card>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                    <h1>{t('patient:managementTitle')}</h1>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => showModal()}
                    >
                        {t('patient:addPatient')}
                    </Button>
                </div>

                <Input.Search
                    placeholder={t('patient:searchPlaceholder')}
                    allowClear
                    enterButton={<SearchOutlined />}
                    size="large"
                    onSearch={handleSearch}
                    style={{ marginBottom: 16, maxWidth: 400 }}
                />

                <Table
                    columns={columns}
                    dataSource={patients}
                    rowKey="_id"
                    loading={loading}
                    scroll={{ x: 'max-content' }}
                    pagination={pagination}
                    onChange={handleTableChange}
                />
            </Card>

            <Modal
                title={editingPatient ? t('patient:editPatient') : t('patient:addPatient')}
                open={modalVisible}
                onCancel={handleCancel}
                footer={null}
                width={800}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="name"
                                label={t('patient:fullName')}
                                rules={[{ required: true, message: t('patient:validation.nameRequired') }]}
                            >
                                <Input placeholder={t('patient:placeholders.name')} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="age"
                                label={t('patient:age')}
                                rules={[{ required: true, message: t('patient:validation.ageRequired') }]}
                            >
                                <Input type="number" placeholder={t('patient:placeholders.age')} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="gender"
                                label={t('patient:gender')}
                                rules={[{ required: true, message: t('patient:validation.genderRequired') }]}
                            >
                                <Select placeholder={t('patient:selectGender')}>
                                    <Option value="Male">{t('patient:male')}</Option>
                                    <Option value="Female">{t('patient:female')}</Option>
                                    <Option value="Other">{t('patient:other')}</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="dateOfBirth"
                                label={t('patient:dateOfBirth')}
                            >
                                <Input type="date" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="contact"
                                label={t('patient:contact')}
                                rules={[{ required: true, message: t('patient:validation.contactRequired') }]}
                            >
                                <Input placeholder={t('patient:placeholders.contact')} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="email"
                                label={t('patient:email')}
                                rules={[{ type: 'email', message: t('patient:validation.emailInvalid') }]}
                            >
                                <Input placeholder={t('patient:placeholders.email')} />
                            </Form.Item>
                        </Col>
                    </Row>

                    {!isDentist && (
                        <Form.Item
                            name="dentistIds"
                            label={t('patient:assignedDentists', 'Assigned Dentists')}
                            rules={[{ required: true, message: 'Please select at least one dentist' }]}
                        >
                            <Select
                                mode="multiple"
                                placeholder="Select dentists"
                                showSearch
                                optionFilterProp="children"
                            >
                                {dentists.map(dentist => (
                                    <Option key={dentist._id} value={dentist._id}>
                                        {dentist.name} - {dentist.specialization}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}

                    <Form.Item name={['address', 'street']} label={t('patient:streetAddress')}>
                        <Input placeholder={t('patient:placeholders.street')} />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name={['address', 'city']} label={t('patient:city')}>
                                <Input placeholder={t('patient:placeholders.city')} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name={['address', 'state']} label={t('patient:state')}>
                                <Input placeholder={t('patient:placeholders.state')} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name={['address', 'zipCode']} label={t('patient:zipCode')}>
                                <Input placeholder={t('patient:placeholders.zipCode')} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name={['medicalHistory', 'bloodType']} label={t('patient:bloodType')}>
                        <Select placeholder={t('patient:selectBloodType')}>
                            <Option value="A+">A+</Option>
                            <Option value="A-">A-</Option>
                            <Option value="B+">B+</Option>
                            <Option value="B-">B-</Option>
                            <Option value="AB+">AB+</Option>
                            <Option value="AB-">AB-</Option>
                            <Option value="O+">O+</Option>
                            <Option value="O-">O-</Option>
                            <Option value="Unknown">Unknown</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item name={['medicalHistory', 'allergies']} label={t('patient:allergies')}>
                        <Select mode="tags" placeholder={t('patient:enterAllergies')} />
                    </Form.Item>

                    <Form.Item name={['medicalHistory', 'chronicDiseases']} label={t('patient:chronicDiseases')}>
                        <Select mode="tags" placeholder={t('patient:enterChronicDiseases')} />
                    </Form.Item>

                    <Form.Item name={['dentalHistory', 'notes']} label={t('patient:dentalHistoryNotes')}>
                        <TextArea rows={3} placeholder={t('patient:enterDentalHistory')} />
                    </Form.Item>

                    <Form.Item>
                        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                            <Button onClick={handleCancel}>{t('common:cancel')}</Button>
                            <Button type="primary" htmlType="submit">
                                {editingPatient ? t('patient:update') : t('patient:create')}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default Patients;
